import { memo, useMemo } from "react";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { resolveGscDateRange } from "@/lib/date-utils";
import {
  formatGscChartTick,
  formatGscCount,
  formatGscCtr,
  formatGscPosition,
} from "@/lib/gsc-display";
import {
  formatGscTrendBucketLabel,
  getChartSeriesColor,
  toSafeSeriesKey,
  type GscTrendChartRow,
} from "@/lib/gsc-trend";
import { cn } from "@/lib/utils";
import {
  useGscTrend,
  type GscTrendParams,
} from "@/hooks/useGscData";
import {
  GSC_COMPARE_METRIC_LABELS,
  GSC_DEFAULT_COMPARE_METRIC,
  type GscCompareMetric,
  type GscDateRangeOption,
  type GscTrendPoint,
  type UrlActiveFilters,
} from "@/types/gsc";

export interface CompareTrendChartProps {
  selectedDomains: readonly string[];
  availableDomains: readonly string[];
  activeMetric: GscCompareMetric;
  dateRange: GscDateRangeOption;
  customFrom?: string;
  customTo?: string;
  className?: string;
}

const EMPTY_ACTIVE_FILTERS = {} as UrlActiveFilters;
const CHART_HEIGHT_PX = 350;

const METRIC_VALUE_KEY = {
  clicks: "clicks",
  impressions: "impressions",
  ctr: "ctr",
  position: "avg_position",
} as const satisfies Record<
  GscCompareMetric,
  keyof Pick<GscTrendPoint, "clicks" | "impressions" | "ctr" | "avg_position">
>;

const formatPrimaryMetric = (
  metric: GscCompareMetric,
  value: number,
): string => {
  if (metric === "ctr") return formatGscCtr(value);
  if (metric === "position") return formatGscPosition(value);
  return formatGscCount(value);
};

const formatYAxisTick = (metric: GscCompareMetric, value: number): string => {
  if (metric === "ctr") return `${Number(value).toFixed(0)}%`;
  if (metric === "position") return formatGscPosition(value);
  return formatGscChartTick(value);
};

const resolveRangeDayCount = (
  dateRange: GscDateRangeOption,
  customFrom?: string,
  customTo?: string,
): number => {
  const { dateFrom, dateTo } = resolveGscDateRange(
    dateRange,
    customFrom,
    customTo,
  );
  const from = parseISO(`${dateFrom}T00:00:00`);
  const to = parseISO(`${dateTo}T00:00:00`);
  return Math.max(1, differenceInCalendarDays(to, from) + 1);
};

/**
 * Pivot flat `{ bucket_date, domain, metrics… }` rows into Recharts series rows.
 *
 * NOTE: `get_gsc_trend` must `GROUP BY bucket_date, domain` when `p_domains`
 * is set. If the RPC collapses domains into a single total per bucket, every
 * series will share the same values — fix the RPC, not this pivot.
 */
const buildCompareTrendRows = (
  points: readonly GscTrendPoint[],
  selectedDomains: readonly string[],
  availableDomains: readonly string[],
  activeMetric: GscCompareMetric,
): { rows: GscTrendChartRow[]; series: CompareTrendSeries[] } => {
  const series: CompareTrendSeries[] = selectedDomains.map((domain, index) => {
    const catalogIndex = availableDomains.indexOf(domain);
    return {
      key: toSafeSeriesKey(domain, index),
      domain,
      color: getChartSeriesColor(catalogIndex >= 0 ? catalogIndex : index),
    };
  });

  if (points.length === 0 || series.length === 0) {
    return { rows: [], series };
  }

  const seriesByDomain = new Map(
    series.map((item) => [item.domain, item] as const),
  );
  const valueKey = METRIC_VALUE_KEY[activeMetric];
  const valuesByBucketAndSeries = new Map<string, number | null>();

  for (const point of points) {
    const seriesItem = seriesByDomain.get(point.domain);
    if (!seriesItem) continue;

    let value: number | null = point[valueKey] ?? 0;
    // Avg position 0 = no impressions that day — treat as a gap (reversed axis
    // would otherwise plot 0 above rank 1 and create a false ceiling).
    if (activeMetric === "position" && value === 0) {
      value = null;
    }

    valuesByBucketAndSeries.set(
      `${point.bucket_date}|${seriesItem.key}`,
      value,
    );
  }

  const bucketDates = [
    ...new Set(points.map((point) => point.bucket_date)),
  ].sort();

  const rows: GscTrendChartRow[] = bucketDates.map((bucketDate) => {
    const row: GscTrendChartRow = {
      bucketDate,
      dateLabel: formatGscTrendBucketLabel(bucketDate, "day"),
    };

    for (const item of series) {
      const stored = valuesByBucketAndSeries.get(
        `${bucketDate}|${item.key}`,
      );

      if (stored === undefined) {
        row[item.key] = activeMetric === "position" ? null : 0;
      } else {
        row[item.key] = stored;
      }
    }

    return row;
  });

  return { rows, series };
};

interface CompareTrendSeries {
  key: string;
  domain: string;
  color: string;
}

const CompareTrendChartSkeleton = () => (
  <Card className="border-border shadow-none">
    <CardHeader className="pb-2">
      <Skeleton className="h-5 w-64" />
    </CardHeader>
    <CardContent>
      <Skeleton className="h-[350px] w-full" />
    </CardContent>
  </Card>
);

function CompareTrendChartComponent({
  selectedDomains,
  availableDomains,
  activeMetric = GSC_DEFAULT_COMPARE_METRIC,
  dateRange,
  customFrom,
  customTo,
  className,
}: CompareTrendChartProps) {
  const safeSelectedDomains = Array.isArray(selectedDomains)
    ? selectedDomains
    : [];
  const safeAvailableDomains = Array.isArray(availableDomains)
    ? availableDomains
    : [];

  const trendParams = useMemo((): GscTrendParams => {
    return {
      dateRange,
      customFrom,
      customTo,
      activeFilters: EMPTY_ACTIVE_FILTERS,
      granularity: "day",
      trendDomains:
        safeSelectedDomains.length > 0 ? [...safeSelectedDomains] : null,
    };
  }, [customFrom, customTo, dateRange, safeSelectedDomains]);

  const {
    data: points = [],
    isPending,
    isFetching,
  } = useGscTrend(trendParams, {
    enabled: safeSelectedDomains.length > 0,
  });

  const dayCount = useMemo(
    () => resolveRangeDayCount(dateRange, customFrom, customTo),
    [customFrom, customTo, dateRange],
  );

  const metricLabel = GSC_COMPARE_METRIC_LABELS[activeMetric];

  const { rows, series } = useMemo(
    () =>
      buildCompareTrendRows(
        points,
        safeSelectedDomains,
        safeAvailableDomains,
        activeMetric,
      ),
    [activeMetric, points, safeAvailableDomains, safeSelectedDomains],
  );

  const chartConfig = useMemo(() => {
    const config: ChartConfig = {};
    for (const item of series) {
      config[item.key] = {
        label: item.domain,
        color: item.color,
      };
    }
    return config;
  }, [series]);

  if (safeSelectedDomains.length === 0) {
    return null;
  }

  if (isPending && points.length === 0) {
    return (
      <div className={className} aria-busy aria-label="Loading compare trend">
        <CompareTrendChartSkeleton />
      </div>
    );
  }

  const hasData = rows.length > 0 && series.length > 0;

  return (
    <Card
      className={cn(
        "border-border shadow-none",
        isFetching && "pointer-events-none opacity-60",
        className,
      )}
      aria-busy={isFetching}
      aria-label="Cross-domain compare trend"
    >
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold tracking-tight">
          {dayCount}-Day {metricLabel} — Cross-Domain
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4 pl-2">
        {hasData ? (
          <>
            <div
              className="w-full"
              style={{ height: CHART_HEIGHT_PX }}
            >
              <ChartContainer config={chartConfig} className="h-full w-full">
                <LineChart
                  data={rows}
                  margin={{ top: 8, right: 12, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical />
                  <XAxis
                    dataKey="dateLabel"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    minTickGap={28}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    width={48}
                    reversed={activeMetric === "position"}
                    domain={
                      activeMetric === "position" ? [1, "auto"] : ["auto", "auto"]
                    }
                    allowDecimals={activeMetric !== "position"}
                    tickFormatter={(value) =>
                      formatYAxisTick(activeMetric, Number(value))
                    }
                  />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        indicator="line"
                        formatter={(value, name) => {
                          if (value == null || Number.isNaN(Number(value))) {
                            return null;
                          }
                          const config = chartConfig[String(name)];
                          return (
                            <div className="flex w-full items-center justify-between gap-3">
                              <div className="flex min-w-0 items-center gap-2">
                                <span
                                  className="size-2 shrink-0 rounded-full"
                                  style={{
                                    backgroundColor:
                                      config?.color ??
                                      "var(--muted-foreground)",
                                  }}
                                  aria-hidden
                                />
                                <span className="truncate text-muted-foreground">
                                  {config?.label ?? name}
                                </span>
                              </div>
                              <span className="font-mono font-medium tabular-nums text-foreground">
                                {formatPrimaryMetric(
                                  activeMetric,
                                  Number(value),
                                )}
                              </span>
                            </div>
                          );
                        }}
                      />
                    }
                  />
                  {series.map((item) => (
                    <Line
                      key={item.key}
                      type="monotone"
                      dataKey={item.key}
                      name={item.key}
                      stroke={`var(--color-${item.key})`}
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                      connectNulls={activeMetric !== "position"}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ChartContainer>
            </div>

            <ul
              className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 px-2"
              aria-label="Domain series legend"
            >
              {series.map((item) => (
                <li
                  key={item.key}
                  className="inline-flex max-w-[14rem] items-center gap-1.5 text-xs text-muted-foreground"
                >
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ backgroundColor: item.color }}
                    aria-hidden
                  />
                  <span className="truncate">{item.domain}</span>
                </li>
              ))}
            </ul>
          </>
        ) : (
          <div className="flex h-[350px] items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 px-6 text-center text-sm text-muted-foreground">
            No trend data for the selected domains and period.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export const CompareTrendChart = memo(CompareTrendChartComponent);
CompareTrendChart.displayName = "CompareTrendChart";
